import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { CockpitWorkerCard } from '../lib/cockpit-view';
import { WORKER_BOARD_STATE_LABEL, workerAssignment, workerBoardState } from '../lib/worker-board';
import '../terminal-workspace.css';

/** Set on <html> while a Worker terminal is presented, so the page behind it cannot scroll. */
export const TERMINAL_MODAL_OPEN_CLASS = 'terminal-modal-open';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function insideTerminal(element: Element | null): boolean {
  return element instanceof Element && element.closest('.xterm') !== null;
}

/**
 * One mounted Worker surface with three presentations: hidden, a modal, and full screen.
 *
 * Presentation is only this element's class and role. The children are rendered in exactly
 * one place and never move, so xterm, its Realtime lease, its sequence buffer and the PTY
 * behind it survive opening, full screen, returning and closing. No portal, no second
 * terminal, no reconnect.
 */
export function WorkerTerminalWorkspace({ card, open, onClose, children }: {
  card: CockpitWorkerCard; open: boolean; onClose: () => void; children: ReactNode;
}) {
  const workspace = useRef<HTMLElement>(null);
  const [fullScreen, setFullScreen] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) setFullScreen(false);
  }, [open]);

  // Opening: lock the page behind, move focus into the dialog, and remember where it came
  // from. Closing hands focus back to that control (the Worker card's Terminal button).
  useEffect(() => {
    if (!open) return undefined;
    // Safari does not focus a clicked button, so fall back to this Worker's Terminal action.
    const active = document.activeElement;
    const opener = active instanceof HTMLElement && active !== document.body
      ? active
      : document.querySelector<HTMLElement>(`[aria-controls="${workspace.current?.id ?? ''}"]`);
    const root = document.documentElement;
    root.classList.add(TERMINAL_MODAL_OPEN_CLASS);
    window.requestAnimationFrame(() => {
      const node = workspace.current;
      if (node !== null && !node.contains(document.activeElement)) node.focus({ preventScroll: true });
    });
    return () => {
      root.classList.remove(TERMINAL_MODAL_OPEN_CLASS);
      if (opener !== null && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      // While xterm owns focus, Escape and Tab are terminal input (vim, shells, TUIs).
      // The visible buttons are the reliable way out in that case.
      if (insideTerminal(document.activeElement) || event.defaultPrevented) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        if (fullScreen) setFullScreen(false);
        else onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = workspace.current;
      if (node === null) return;
      const focusables = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)]
        .filter((element) => element.getClientRects().length > 0 || element === document.activeElement);
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === node)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !node.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, fullScreen]);

  const workspaceId = `worker-${card.workerNumber}-workspace`;
  const titleId = `${workspaceId}-title`;
  const state = workerBoardState(card);
  const unassigned = workerAssignment(card) === 'unassigned';
  const identity = [card.machine, [card.runtime, card.model].filter(Boolean).join(' · ')]
    .filter((part): part is string => part !== null && part !== '');
  const presentation = !open ? '' : fullScreen ? ' is-modal is-full-screen' : ' is-modal';

  return (
    <section ref={workspace} id={workspaceId} hidden={!open} tabIndex={-1}
      className={`terminal-workspace${presentation}`}
      role={open ? 'dialog' : 'region'} aria-modal={open || undefined} aria-labelledby={titleId}
      data-worker-id={card.workerId} data-terminal-session={card.sessionId ?? undefined}>
      <header className="terminal-workspace-heading">
        <div className="terminal-workspace-title">
          <span className="worker-no" aria-hidden="true">{card.workerNumber}</span>
          <div>
            <h2 id={titleId}>Worker {card.workerNumber} · {card.workerName}</h2>
            {identity.length > 0 && <p className="terminal-workspace-identity">{identity.join('  ·  ')}</p>}
          </div>
        </div>
        <div className="terminal-workspace-context">
          <span className={`worker-chip chip-${state}`}>{WORKER_BOARD_STATE_LABEL[state]}</span>
          {unassigned
            ? <span className="worker-chip chip-unassigned">Unassigned</span>
            : card.project !== null && <span className="worker-tag">{card.project}</span>}
          {card.activity !== null && <span className="terminal-workspace-task" title={card.activity}>{card.activity}</span>}
        </div>
        <span className="terminal-workspace-controls">
          <button type="button" onClick={() => setFullScreen((current) => !current)}
            aria-pressed={fullScreen} aria-label={`${fullScreen ? 'Exit full screen' : 'Full screen'} for Worker ${card.workerNumber}`}>
            {fullScreen ? 'Exit full screen' : 'Full screen'}
          </button>
          <button type="button" className="terminal-workspace-close" onClick={onClose}
            aria-label={`Close terminal; Worker ${card.workerNumber} ${card.workerName} keeps running`}>
            Close
          </button>
        </span>
      </header>
      <div className="terminal-workspace-body">
        {children}
      </div>
      {open && (
        <footer className="terminal-workspace-footer">
          <small>Closing only hides this view. The Worker, its AI session and its terminal keep running.</small>
          <small>
            <kbd>Esc</kbd> {fullScreen ? 'leaves full screen' : 'closes'} when the terminal does not have the keyboard ·{' '}
            <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>F6</kbd> moves the keyboard out of the terminal
          </small>
        </footer>
      )}
    </section>
  );
}
